import { useEffect, useMemo, useState } from "react";
import {
  FaRegUser,
  FaDog,
  FaChevronDown,
  FaChevronUp,
  FaClipboardList,
  FaInfoCircle,
} from "react-icons/fa";
import { IoCalendarOutline, IoTimeOutline } from "react-icons/io5";
import { getAccessToken } from "../../../storage/storage";
import * as Yup from "yup";
import { useFormik } from "formik";
import axios from "axios";
import toast from "react-hot-toast";
import { ButtonLoader4 } from "../../../components/ButtonLoader";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { getApiErrorMessage } from "../../../utils/apiError";
import { IoMdArrowBack } from "react-icons/io";
import Swal from "sweetalert2";
import ToggleSwitch from "../../../components/ToggleSwitch";

// Normalizes any time string to HH:MM (24-hour) for <input type="time">
// Handles: "4:06 PM", "16:06", "03:06:00", null/undefined
const toTimeInput = (value) => {
  if (!value) return "";
  // Strip seconds if present (HH:MM:SS → HH:MM)
  const stripped = value.trim().replace(/^(\d{1,2}:\d{2}):\d{2}$/, "$1");
  // Already 24-hour HH:MM
  if (/^\d{2}:\d{2}$/.test(stripped)) return stripped;
  // 12-hour with AM/PM
  const match = stripped.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
  if (match) {
    let hours = parseInt(match[1], 10);
    const minutes = match[2];
    const period = match[3].toUpperCase();
    if (period === "AM" && hours === 12) hours = 0;
    if (period === "PM" && hours !== 12) hours += 12;
    return `${String(hours).padStart(2, "0")}:${minutes}`;
  }
  return stripped;
};

// Maps each Preparation Requirement toggle to the property amenity flag that
// gates its visibility on this form.
const PREPARATION_REQUIREMENT_FIELDS = [
  { requirementKey: "baby_cot_required", amenityKey: "has_baby_cot", label: "Baby cot required", icon: "🍼" },
  { requirementKey: "high_chair_required", amenityKey: "has_high_chair", label: "High chair required", icon: "🪑" },
  { requirementKey: "sofa_bed_required", amenityKey: "has_sofa_bed", label: "Sofa bed to be prepared", icon: "🛋️" },
  { requirementKey: "hot_tub_required", amenityKey: "has_hot_tub", label: "Hot tub to be prepared", icon: "🛁" },
  { requirementKey: "sauna_required", amenityKey: "has_sauna", label: "Sauna to be prepared", icon: "🧖" },
  { requirementKey: "log_burner_required", amenityKey: "has_log_burner", label: "Log burner to be laid", icon: "🔥" },
];

// Unlike the fields above, this toggle isn't gated by any property amenity —
// it's always shown on the stay form.
const ALWAYS_SHOWN_REQUIREMENT_FIELDS = [
  { requirementKey: "lights_on_for_guests", label: "Lights on for guests", icon: "💡" },
];

// Must mirror the backend's GuestStay.has_preparation_requirements definition
// exactly, so the "N requirements set" badge always agrees with the server.
const countActiveRequirements = (values, visibleFields) => {
  let count = visibleFields.filter((f) => values[f.requirementKey]).length;
  count += ALWAYS_SHOWN_REQUIREMENT_FIELDS.filter((f) => values[f.requirementKey]).length;
  if (Number(values.number_of_vehicles) > 0) count += 1;
  if (values.special_occasion && values.special_occasion !== "NONE") count += 1;
  if (values.additional_notes_for_cleaner?.trim().length > 0) count += 1;
  return count;
};

const ContactFormSchema = Yup.object({
  property: Yup.string().required("Select option"),
  lead_guest_name: Yup.string().required("Enter lead guest name")
  .max(50, "First name must be at most 50 characters")
    .test("not-empty", "First name cannot be only spaces", value => value && value.trim().length > 0)
    .matches(
      /^[A-Za-z\s'-]+$/,
      "First name can only contain letters, spaces, hyphens, and apostrophes"
    )
    .test("min-trimmed-length", "First name must be at least 2 characters", value => value && value.trim().length >= 2),
  
  number_of_adults: Yup.number()
    .typeError("Number of adults must be a number")
    .required("Enter number of adults")
    .positive("Number of adults must be positive")
    .max(20, "Number of adults cannot exceed 20"),

  number_of_children: Yup.number()
    .typeError("Number of children must be a number")
    .required("Enter number of children")
    .min(0, "Number of children cannot be negative")
    .max(20, "Number of children cannot exceed 20"),

  number_of_infants: Yup.number()
    .typeError("Number of infants must be a number")
    .required("Enter number of infants")
    .min(0, "Number of infants cannot be negative")
    .max(20, "Number of infants cannot exceed 20"),

  number_of_pets: Yup.number()
    .typeError("Number of pets must be a number")
    .required("Enter number of pets")
    .min(0, "Number of pets cannot be negative")
    .max(20, "Number of pets cannot exceed 20"),

  check_in_date: Yup.date().required("Enter check-in date"),

  check_out_date: Yup.date()
    .required("Enter check-out date")
    .test(
      "is-after-checkin",
      "Check-out date must be after check-in date",
      function (value) {
        const { check_in_date } = this.parent;
        return value && check_in_date && new Date(value) > new Date(check_in_date);
      }
    ),
  check_in_time: Yup.string().nullable().optional(),
  check_out_time: Yup.string().nullable().optional(),
  eta: Yup.string().nullable().optional(),
 // cleaner: Yup.string().required("Select cleaner"),

  number_of_vehicles: Yup.number()
    .typeError("Number of vehicles must be a number")
    .min(0, "Number of vehicles cannot be negative")
    .max(20, "Number of vehicles cannot exceed 20"),
  special_occasion: Yup.string().oneOf(["NONE", "BIRTHDAY", "ANNIVERSARY", "CHRISTMAS"]),
  additional_notes_for_cleaner: Yup.string().max(2500, "Notes cannot exceed 2500 characters"),
});

export default function GuestStayForm() {
  const token = getAccessToken();
  const BASE_URL = process.env.REACT_APP_BASE_URL;
  const [loading, setLoading] = useState(false);
  const [options, setOptions] = useState([]);
  const [cleaners, setCleaners] = useState([]);
  const [cleanersError, setCleanersError] = useState("");
  const navigate = useNavigate();
  const location = useLocation();
  const stay = location.state?.stay; // Stay data from location
  const cleanerEdit = JSON.parse(sessionStorage.getItem("cleanerEdit")); // Retr
  const [selectedPropertyId, setSelectedPropertyId] = useState("");
  const [isPrepExpanded, setIsPrepExpanded] = useState(false);

  // Get the previous path from location state or default to "/stays"
  const previousPath = location.state?.from || "/stays";

  const selectedProperty = useMemo(
    () => options.find((p) => p.id === selectedPropertyId) || null,
    [options, selectedPropertyId]
  );
  // Only render toggles whose amenity is enabled on the selected property.
  const visibleRequirementFields = useMemo(
    () => PREPARATION_REQUIREMENT_FIELDS.filter((f) => selectedProperty?.[f.amenityKey]),
    [selectedProperty]
  );

  const initialValues = {
    property: stay?.property || "",
    lead_guest_name: stay?.lead_guest_name || "",
    number_of_adults: stay?.number_of_adults ?? "",
    number_of_children: stay?.number_of_children ?? "",
    number_of_infants: stay?.number_of_infants ?? "",
    number_of_pets: stay?.number_of_pets ?? "", // This will preserve 0
    check_in_date: stay?.check_in_date || "",
    check_out_date: stay?.check_out_date || "",
    check_in_time: toTimeInput(stay?.check_in_time),
    check_out_time: toTimeInput(stay?.check_out_time),
    eta: toTimeInput(stay?.eta),
    cleaner: stay?.cleaner || "",
    baby_cot_required: stay?.baby_cot_required || false,
    high_chair_required: stay?.high_chair_required || false,
    sofa_bed_required: stay?.sofa_bed_required || false,
    hot_tub_required: stay?.hot_tub_required || false,
    sauna_required: stay?.sauna_required || false,
    log_burner_required: stay?.log_burner_required || false,
    lights_on_for_guests: stay?.lights_on_for_guests || false,
    number_of_vehicles: stay?.number_of_vehicles ?? 0,
    special_occasion: stay?.special_occasion || "NONE",
    additional_notes_for_cleaner: stay?.additional_notes_for_cleaner || "",
  };

  useEffect(() => {
    axios
      .get(`${BASE_URL}/api/properties/`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      })
      .then((response) => setOptions(response.data))
      .catch((error) => console.error("Error fetching options:", error));
  }, []);

  const handlePropertyChange = (e) => {
  const id = parseInt(e.target.value);
  const newProperty = options.find((p) => p.id === id) || null;
  setSelectedPropertyId(id);
  setFieldValue("property", id);
  // Reset any requirement toggle that's no longer visible on the new
  // property (its amenity isn't enabled there), so a stale true value
  // can't be submitted for a hidden field.
  PREPARATION_REQUIREMENT_FIELDS.forEach((f) => {
    if (!newProperty?.[f.amenityKey] && values[f.requirementKey]) {
      setFieldValue(f.requirementKey, false);
    }
  });
};
useEffect(() => {
  if (cleanerEdit && stay?.property) {
    setSelectedPropertyId(stay.property); // numeric ID
  }
  console.log("stay",stay);
  console.log("number_of_pets",values.number_of_pets);
}, [cleanerEdit, stay]);

// Default expanded on a new stay; default collapsed on edit unless the
// stay already has >=1 requirement set. Uses the full static field list
// (not visibleRequirementFields) since the property fetch above may not
// have resolved yet at mount — basing this on amenity-visibility here
// would race with that fetch.
useEffect(() => {
  if (!cleanerEdit) {
    setIsPrepExpanded(true);
  } else {
    const initialCount = countActiveRequirements(initialValues, PREPARATION_REQUIREMENT_FIELDS);
    setIsPrepExpanded(initialCount > 0);
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [cleanerEdit]);
  useEffect(() => {
    if (selectedPropertyId) {
      // Ensure API calls only when a valid property is selected
      axios
        .get(
          `${BASE_URL}/api/cleaners-by-property/?property_id=${selectedPropertyId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        )
        .then((response) => {
          setCleaners(response.data);
          setCleanersError("");
        })
        .catch((error) => {
          console.error("Error fetching cleaners:", error);
          setCleaners([]);
          setCleanersError(getApiErrorMessage(error, "Couldn't load cleaners."));
        });
    }
  }, [selectedPropertyId]);

  const { values, errors, handleChange, handleSubmit, setFieldValue, touched } =
    useFormik({
      initialValues: initialValues,
      validationSchema: ContactFormSchema,
      onSubmit: async (values, action) => {
        setLoading(true);

        const payload = {
          ...values,
          property: values.property ? parseInt(values.property) : null,
          cleaner: values.cleaner ? parseInt(values.cleaner) : null,
          check_in_time: values.check_in_time || null,
          check_out_time: values.check_out_time || null,
          eta: values.eta || null,
          number_of_vehicles: Number(values.number_of_vehicles) || 0,
        };

        try {
          let response;

          if (cleanerEdit) {
            // If edit mode is true, update the existing stay
            response = await axios.put(
              `${BASE_URL}/api/guest-stay/update/${stay.id}/`,
              payload,
              {
                headers: {
                  Authorization: `Bearer ${token}`,
                  "Content-Type": "application/json",
                },
              }
            );
          } else if (!cleanerEdit) {
            // If not in edit mode, create a new stay
            response = await axios.post(
              `${BASE_URL}/api/guest-stays/`,
              payload,
              {
                headers: {
                  Authorization: `Bearer ${token}`,
                  "Content-Type": "application/json",
                },
              }
            );
          }

          Swal.fire({
            icon: "success",
            title: cleanerEdit
              ? "Data Updated Successfully"
              : response.data.message || "Data Added Successfully",
            timer: 2000,
            showConfirmButton: false,
            position: "center",
          });
          sessionStorage.removeItem("cleanerEdit");
          action.resetForm();
          navigate(previousPath); // Navigate back to previous page
        } catch (error) {
          Swal.fire({
            icon: "error",
            title:
              error.response?.data?.error ||
              error.response?.data?.non_field_errors ||
              "Something went wrong!",
            timer: 2000,
            showConfirmButton: false,
            position: "center",
          });
        } finally {
          setLoading(false);
        }
      },
    });

  // Auto-suggest "Baby cot required" once infants are added. Guarded by
  // Formik's own touched state so a deliberate owner un-check is never
  // stomped back on by a later render (e.g. adjusting another field).
  useEffect(() => {
    if (Number(values.number_of_infants) > 0 && !touched.baby_cot_required) {
      setFieldValue("baby_cot_required", true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.number_of_infants]);

  // Function to handle back navigation
 const handleBackClick = () => {
    // If there's previous state, use it, otherwise go back in history
    if (location.state?.from) {
      navigate(previousPath);
    } else {
      navigate(-1); // Go back one step in history
    }
  };

  return (
    <div className="relative">
      {/* Back Button */}
      <button
        onClick={handleBackClick}
        className="flex items-center gap-2 absolute lg:top-2 top-[-30px] left-6 text-xl font-bold text-[#586989] hover:text-blue-600"
      >
        <IoMdArrowBack />
        <p>Back</p>
      </button>
      <div className="max-w-2xl mx-auto h-fit mt-10 mb-10">
        <div className="bg-white shadow-lg rounded-lg p-6 w-full">
          <h2 className="md:text-3xl text-xl font-bold text-[#586989] text-center">
            Guest Stays
          </h2>
          <p className="text-gray-600 text-center">
            Manage guest stays for your properties
          </p>

          {/* Property Selection */}
          <p className="mt-6 text-xs font-semibold tracking-wide text-gray-500 uppercase pb-2 border-b border-gray-200">
            Property
          </p>
          <div className="mt-3 flex flex-col gap-2">
            <label className="font-medium">Select property*</label>
            <select
              name="property"
              value={selectedPropertyId || ""}  // controlled
              onChange={handlePropertyChange}
              className="w-full p-2 border rounded-md"
            >
              <option value="">Select a Property</option>
              {options.map((property) => (
                <option key={property.id} value={property.id}>
                  {property.name}
                </option>
              ))}
            </select>
            {errors.property && touched.property && (
              <div className="text-[#DC2626] text-[12px]">
                {errors.property}
              </div>
            )}
          </div>

          {/* Guest Details Form */}
          <div className="mt-2">
            <div>
              <p className="mt-4 text-xs font-semibold tracking-wide text-gray-500 uppercase pb-2 border-b border-gray-200 flex items-center gap-2">
                Guest Details
                {cleanerEdit && stay?.guest_breakdown_source && (
                  <span
                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full font-medium text-xs normal-case tracking-normal ${
                      stay.guest_breakdown_source === "LODGIFY"
                        ? "bg-green-100 text-green-800"
                        : "bg-gray-100 text-gray-800"
                    }`}
                  >
                    {stay.guest_breakdown_source === "LODGIFY"
                      ? "Synced from Lodgify"
                      : "Entered manually"}
                  </span>
                )}
              </p>

              {/* Lead Guest Name */}
              <div className="mt-3 space-y-3 w-full">
                <label className="block">Lead guest name*</label>
                <input
                  placeholder="Enter lead guest name"
                  name="lead_guest_name"
                  value={values.lead_guest_name}
                  onChange={handleChange}
                  className="w-full p-2 border rounded-lg"
                />
                {errors.lead_guest_name && touched.lead_guest_name && (
                  <div className="text-[#DC2626] text-[12px]">
                    {errors.lead_guest_name}
                  </div>
                )}
              </div>

              {/* Number of Adults & Children */}
              <div className="flex md:flex-row flex-col gap-4 mt-4">
                <div className="flex-1">
                  <label>Number of adults*</label>
                  <input
                    type="text"
                    name="number_of_adults"
                    value={values.number_of_adults}
                    onChange={handleChange}
                    className="w-full p-2 border rounded-lg"
                  />
                  {errors.number_of_adults && touched.number_of_adults && (
                    <div className="text-[#DC2626] text-[12px] pt-1">
                      {errors.number_of_adults}
                    </div>
                  )}
                </div>
                <div className="flex-1">
                  <label>Number of children*</label>
                  <input
                    type="text"
                    name="number_of_children"
                    value={values.number_of_children}
                    onChange={handleChange}
                    className="w-full p-2 border rounded-lg"
                  />
                  {errors.number_of_children && touched.number_of_children && (
                    <div className="text-[#DC2626] text-[12px] pt-1">
                      {errors.number_of_children}
                    </div>
                  )}
                </div>
              </div>

              {/* Number of Infants & Pets */}
              <div className="flex md:flex-row flex-col gap-4 mt-4">
                <div className="flex-1">
                  <label>Number of infants*</label>
                  <input
                    type="text"
                    name="number_of_infants"
                    value={values.number_of_infants}
                    onChange={handleChange}
                    className="w-full p-2 border rounded-lg"
                  />
                  {errors.number_of_infants && touched.number_of_infants && (
                    <div className="text-[#DC2626] text-[12px] pt-1">
                      {errors.number_of_infants}
                    </div>
                  )}
                </div>
                <div className="flex-1">
                  <label>Number of pets*</label>
                  <input
                    type="text"
                    name="number_of_pets"
                    value={values.number_of_pets}
                    onChange={handleChange}
                    className="w-full p-2 border rounded-lg"
                  />
                  {errors.number_of_pets && touched.number_of_pets && (
                    <div className="text-[#DC2626] text-[12px] pt-1">
                      {errors.number_of_pets}
                    </div>
                  )}
                </div>
              </div>

              {/* Check-in & Check-out Dates */}
              <p className="mt-5 text-xs font-semibold tracking-wide text-gray-500 uppercase pb-2 border-b border-gray-200">
                Stay Dates &amp; Times
              </p>
              <div className="flex md:flex-row flex-col gap-4 mt-3">
                <div className="flex-1">
                  <label>Check-in date*</label>
                  <input
                    type="date"
                    name="check_in_date"
                    value={values.check_in_date}
                    onChange={handleChange}
                    className="w-full p-2 border rounded-lg"
                  />
                  {errors.check_in_date && touched.check_in_date && (
                    <div className="text-[#DC2626] text-[12px] pt-1">
                      {errors.check_in_date}
                    </div>
                  )}
                </div>
                <div className="flex-1">
                  <label>Check-out date*</label>
                  <input
                    type="date"
                    name="check_out_date"
                    value={values.check_out_date}
                    onChange={handleChange}
                    className="w-full p-2 border rounded-lg"
                  />
                  {errors.check_out_date && touched.check_out_date && (
                    <div className="text-[#DC2626] text-[12px] pt-1">
                      {errors.check_out_date}
                    </div>
                  )}
                </div>
              </div>

              <div className="flex md:flex-row flex-col gap-4 mt-4">
                <div className="flex-1">
                  <label>Check-in time</label>
                  <input
                    type="time"
                    name="check_in_time"
                    value={values.check_in_time}
                    onChange={handleChange}
                    className="w-full p-2 border rounded-lg"
                  />
                  {errors.check_in_time && touched.check_in_time && (
                    <div className="text-[#DC2626] text-[12px] pt-1">
                      {errors.check_in_time}
                    </div>
                  )}
                </div>
                <div className="flex-1">
                  <label>Check-out time</label>
                  <input
                    type="time"
                    name="check_out_time"
                    value={values.check_out_time}
                    onChange={handleChange}
                    className="w-full p-2 border rounded-lg"
                  />
                  {errors.check_out_time && touched.check_out_time && (
                    <div className="text-[#DC2626] text-[12px] pt-1">
                      {errors.check_out_time}
                    </div>
                  )}
                </div>
                <div className="flex-1">
                  <label>Guest ETA</label>
                  <input
                    type="time"
                    name="eta"
                    value={values.eta}
                    onChange={handleChange}
                    className="w-full p-2 border rounded-lg"
                  />
                  {errors.eta && touched.eta && (
                    <div className="text-[#DC2626] text-[12px] pt-1">
                      {errors.eta}
                    </div>
                  )}
                </div>
              </div>

              {/* Assign Cleaner */}
              <p className="mt-5 text-xs font-semibold tracking-wide text-gray-500 uppercase pb-2 border-b border-gray-200">
                Cleaner Assignment
              </p>
              <div className="mt-3">
                <label>Assign cleaner</label>
                <select
                  name="cleaner"
                  value={values.cleaner}
                  onChange={(e) =>
                    setFieldValue(
                      "cleaner",
                      e.target.value ? parseInt(e.target.value) : ""
                    )
                  }
                  className="w-full p-2 border rounded-md"
                >
                  <option value="">Please select...</option>
                  {cleaners.map((option) => (
                    <option className="" key={option.id} value={option.id}>
                      {option.first_name} {option.surname}
                    </option>
                  ))}
                </select>

                {errors.cleaner && touched.cleaner && (
                  <div className="text-[#DC2626] text-[12px] h-[6px] pb-2 pt-2">
                    {errors.cleaner}
                  </div>
                )}
                {cleanersError && (
                  <div className="text-[#DC2626] text-[12px] pb-2 pt-2">
                    {cleanersError}
                  </div>
                )}
              </div>

              {/* Preparation Requirements */}
              <p className="mt-5 text-xs font-semibold tracking-wide text-gray-500 uppercase pb-2 border-b border-gray-200">
                Preparation Requirements
              </p>
              <div className="mt-3 rounded-md border border-[#f0d98c] overflow-hidden">
                <div
                  className="flex items-center justify-between gap-2 cursor-pointer bg-[#FFF5CE] px-3 py-2.5"
                  onClick={() => setIsPrepExpanded((prev) => !prev)}
                >
                  <div className="flex items-center gap-2">
                    <FaClipboardList className="text-[#92722a]" />
                    <span className="font-medium text-[#586989]">Stay preparation requirements</span>
                    {(() => {
                      const activeRequirementsCount = countActiveRequirements(
                        values,
                        visibleRequirementFields
                      );
                      return activeRequirementsCount > 0 ? (
                        <span className="text-xs bg-[#586989] text-white rounded-full px-2 py-0.5">
                          {activeRequirementsCount} requirement{activeRequirementsCount === 1 ? "" : "s"} set
                        </span>
                      ) : null;
                    })()}
                  </div>
                  {isPrepExpanded ? <FaChevronUp /> : <FaChevronDown />}
                </div>

                {isPrepExpanded && (
                  <div className="p-4 bg-white">
                    {!selectedPropertyId ? (
                      <p className="text-gray-500 text-xs mb-3">
                        Select a property above to see the preparation requirements available for it.
                      </p>
                    ) : (
                      <p className="flex items-center gap-1.5 text-gray-500 text-xs italic mb-3">
                        <FaInfoCircle className="flex-shrink-0" />
                        Fields shown are based on amenities configured for this property
                      </p>
                    )}

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
                      {[...visibleRequirementFields, ...ALWAYS_SHOWN_REQUIREMENT_FIELDS].map((f) => (
                        <ToggleSwitch
                          key={f.requirementKey}
                          name={f.requirementKey}
                          checked={values[f.requirementKey]}
                          onChange={handleChange}
                          label={f.label}
                          icon={f.icon}
                          className={`border rounded-lg px-3 py-2.5 transition-colors ${
                            values[f.requirementKey]
                              ? "border-[#586989] bg-[#eef1f6]"
                              : "border-gray-200 bg-gray-50"
                          }`}
                        />
                      ))}
                    </div>

                    <div className="flex flex-col gap-4">
                      <div>
                        <label>Number of vehicles in party</label>
                        <select
                          name="number_of_vehicles"
                          value={values.number_of_vehicles}
                          onChange={handleChange}
                          className="w-full p-2 border rounded-md"
                        >
                          {Array.from({ length: 21 }, (_, i) => i).map((n) => (
                            <option key={n} value={n}>
                              {n}
                            </option>
                          ))}
                        </select>
                        {errors.number_of_vehicles && touched.number_of_vehicles && (
                          <div className="text-[#DC2626] text-[12px] pt-1">
                            {errors.number_of_vehicles}
                          </div>
                        )}
                      </div>
                      <div>
                        <label>Special occasion setup</label>
                        <select
                          name="special_occasion"
                          value={values.special_occasion}
                          onChange={handleChange}
                          className="w-full p-2 border rounded-md"
                        >
                          <option value="NONE">None</option>
                          <option value="BIRTHDAY">🎂 Birthday</option>
                          <option value="ANNIVERSARY">💍 Anniversary</option>
                          <option value="CHRISTMAS">🎄 Christmas</option>
                        </select>
                      </div>
                    </div>

                    <div className="mt-4">
                      <label>Additional notes for cleaner</label>
                      <textarea
                        name="additional_notes_for_cleaner"
                        value={values.additional_notes_for_cleaner}
                        onChange={handleChange}
                        maxLength={2500}
                        rows={4}
                        className="w-full p-2 border rounded-lg"
                      />
                      <div className="flex justify-between items-center">
                        {errors.additional_notes_for_cleaner && touched.additional_notes_for_cleaner ? (
                          <div className="text-[#DC2626] text-[12px] pt-1">
                            {errors.additional_notes_for_cleaner}
                          </div>
                        ) : (
                          <span />
                        )}
                        <span className="text-gray-500 text-xs pt-1">
                          {(2500 - values.additional_notes_for_cleaner.length).toLocaleString()} characters remaining
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Submit & Cancel Buttons */}
              <div className="flex justify-end gap-5 mt-6">
                <button
                  onClick={handleBackClick}
                  className="border-2 border-[#106A3A] text-black w-[100px] h-[45px] rounded-md flex items-center justify-center"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmit}
                  type="submit"
                  className="bg-[#586989] text-white w-[140px] h-[45px] flex items-center justify-center rounded-md"
                >
                  {!cleanerEdit ? (
                    <>{loading ? <ButtonLoader4 /> : <>Add Guest Stay</>}</>
                  ) : (
                    <>{loading ? <ButtonLoader4 /> : <>Update Guest Stay</>}</>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}